import { useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  ChevronRight,
  LayoutDashboard,
  List,
  LogOut,
  Mail,
  MapPin,
  Shirt,
  ShoppingBag,
  Settings,
  SlidersHorizontal,
  Store,
  TicketPercent,
  Truck,
  UserCog,
  Users,
} from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';
import { roleHasAccess } from '@rosita/shared';
import { ToastProvider } from '../admin/Toast';
import { ActionMenu } from '../admin/ActionMenu';
import { t } from '../../i18n/pt';
import '../../styles/admin.css';

const navLinkBase =
  'flex items-center gap-3 rounded-[10px] px-3 py-2.5 text-sm transition-colors';
const navLinkIdle =
  'text-admin-sidebar-text hover:bg-admin-sidebar-hover';
const navLinkActive =
  'bg-admin-sidebar-active-bg text-admin-sidebar-active-fg font-semibold hover:bg-admin-sidebar-active-bg';
const subNavLinkBase =
  'flex items-center gap-3 rounded-[10px] px-3 py-2 pl-7 text-sm transition-colors';
const navIconClass = 'h-4 w-4 shrink-0';

function navClass({ isActive }: { isActive: boolean }): string {
  return `${navLinkBase} ${isActive ? navLinkActive : navLinkIdle}`;
}

function subNavClass({ isActive }: { isActive: boolean }): string {
  return `${subNavLinkBase} ${isActive ? navLinkActive : navLinkIdle}`;
}

export function AdminLayout(): JSX.Element {
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const location = useLocation();
  const navigate = useNavigate();

  const roleLabel = user ? t.admin.roles[user.role] : '';
  const initial = (user?.firstName ?? user?.email ?? '?').charAt(0).toUpperCase();

  const canAdmin = user ? roleHasAccess(user.role, 'admin') : false;
  const isSuperAdmin = user?.role === 'super_admin';
  const isOnProducts = location.pathname.startsWith('/admin/produtos');
  const [productsOpen, setProductsOpen] = useState(true);

  return (
    <ToastProvider>
    <div className="admin-root grid min-h-screen grid-cols-1 md:grid-cols-[240px_1fr]">
      <aside className="flex flex-col border-r border-admin-sidebar-border bg-admin-sidebar p-4 text-white">
        <Link
          to="/admin"
          aria-label="Rosita — Backoffice"
          className="flex flex-col items-start gap-2 px-2 pt-2"
        >
          <img
            src="/brand/rosita-logo.webp"
            alt="Rosita"
            className="h-11 w-auto"
            style={{ filter: 'brightness(0) invert(1)' }}
          />
          <span className="text-[10px] font-medium uppercase tracking-[0.12em] text-white/90">
            Backoffice
          </span>
        </Link>
        <nav className="mt-6 flex flex-col gap-1">
          <NavLink to="/admin" end className={navClass}>
            <LayoutDashboard aria-hidden className={navIconClass} />
            <span>Painel</span>
          </NavLink>
          <button
            type="button"
            onClick={() => setProductsOpen((o) => !o)}
            aria-expanded={productsOpen}
            aria-controls="admin-nav-produtos"
            className={`${navLinkBase} justify-between text-left ${
              isOnProducts ? navLinkActive : navLinkIdle
            }`}
          >
            <span className="flex items-center gap-2.5">
              <Shirt aria-hidden className={navIconClass} />
              <span>Produtos</span>
            </span>
            <ChevronRight
              aria-hidden
              className={`h-4 w-4 opacity-70 transition-transform duration-200 ease-out ${
                productsOpen ? 'rotate-90' : ''
              }`}
            />
          </button>
          <div
            id="admin-nav-produtos"
            className={`grid transition-[grid-template-rows] duration-200 ease-out ${
              productsOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
            }`}
          >
            <div className="overflow-hidden">
              <div className="flex flex-col gap-1 pt-1">
                <NavLink to="/admin/produtos" end className={subNavClass}>
                  <List aria-hidden className={navIconClass} />
                  <span>Lista</span>
                </NavLink>
                <NavLink to="/admin/produtos/configuracoes" className={subNavClass}>
                  <Settings aria-hidden className={navIconClass} />
                  <span>Configurações</span>
                </NavLink>
              </div>
            </div>
          </div>
          <NavLink to="/admin/encomendas" className={navClass}>
            <ShoppingBag aria-hidden className={navIconClass} />
            <span>Encomendas</span>
          </NavLink>
          {canAdmin ? (
            <NavLink to="/admin/cupoes" className={navClass}>
              <TicketPercent aria-hidden className={navIconClass} />
              <span>Cupões</span>
            </NavLink>
          ) : null}
          {canAdmin ? (
            <NavLink to="/admin/envios" className={navClass}>
              <Truck aria-hidden className={navIconClass} />
              <span>Envios</span>
            </NavLink>
          ) : null}
          {canAdmin ? (
            <NavLink to="/admin/pontos-de-recolha" className={navClass}>
              <MapPin aria-hidden className={navIconClass} />
              <span>Pontos de recolha</span>
            </NavLink>
          ) : null}
          {canAdmin ? (
            <NavLink to="/admin/clientes" className={navClass}>
              <Users aria-hidden className={navIconClass} />
              <span>Clientes</span>
            </NavLink>
          ) : null}
          {canAdmin ? (
            <NavLink to="/admin/equipa" className={navClass}>
              <UserCog aria-hidden className={navIconClass} />
              <span>Equipa</span>
            </NavLink>
          ) : null}
          {canAdmin ? (
            <NavLink to="/admin/emails" className={navClass}>
              <Mail aria-hidden className={navIconClass} />
              <span>Emails</span>
            </NavLink>
          ) : null}
          {isSuperAdmin ? (
            <NavLink to="/admin/definicoes" className={navClass}>
              <SlidersHorizontal aria-hidden className={navIconClass} />
              <span>Definições</span>
            </NavLink>
          ) : null}
        </nav>
        <div className="mt-auto pt-4">
          <div className="flex items-center gap-3 rounded-admin-lg bg-white p-3">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-admin-blush text-base font-semibold text-admin-accent">
              {initial}
            </span>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-medium text-admin-primary">
                {user?.firstName}
              </div>
              <div className="truncate text-xs text-admin-muted">{roleLabel}</div>
            </div>
            <ActionMenu
              label={t.admin.dashboard}
              items={[
                {
                  label: t.admin.backToStore,
                  icon: Store,
                  onClick: () => navigate('/'),
                },
                {
                  label: t.admin.logout,
                  icon: LogOut,
                  danger: true,
                  onClick: () => {
                    void logout();
                  },
                },
              ]}
            />
          </div>
        </div>
      </aside>
      <main className="overflow-x-auto bg-admin-bg p-8 max-md:p-4">
        <Outlet />
      </main>
    </div>
    </ToastProvider>
  );
}
